import { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth.jsx";
import { questionsAPI, answersAPI, topicsAPI, notificationsAPI, commentsAPI, listingsAPI } from "./api/index.js";
import ProfilePage from "./pages/ProfilePage.jsx";
import MarkdownRenderer from "./components/MarkdownRenderer.jsx";
import MarkdownToolbar from "./components/MarkdownToolbar.jsx";
import Comments from "./components/Comments.jsx";
import SEO from "./components/SEO.jsx";
import SignInPrompt from "./components/SignInPrompt.jsx";
import AdminPage from "./pages/AdminPage.jsx";
import AnalyticsPage from "./pages/AnalyticsPage.jsx";
import LandingPage from "./pages/LandingPage.jsx";
import ListingsPage from "./pages/ListingsPage.jsx";
import ListingDetailPage from "./pages/ListingDetailPage.jsx";
import SubmitListingPage from "./pages/SubmitListingPage.jsx";
import TopicPage from "./pages/TopicPage.jsx";
import SearchPage from "./pages/SearchPage.jsx";

const G = {
  bg: "#0A0A0A", surface: "#111111", border: "#1E1E1E",
  text: "#E4E4DC", muted: "#666", accent: "#00C896",
  purple: "#7C6FFF", danger: "#FF6B6B", warn: "#FFB347",
  font: "'Sora', sans-serif", mono: "'DM Mono', monospace",
};

const Tag = ({ children, color = G.purple }) => (
  <span style={{ background:color+"18", border:`1px solid ${color}44`, color, fontSize:10, padding:"2px 10px", borderRadius:20, fontFamily:G.mono, letterSpacing:0.5, display:"inline-block" }}>{children}</span>
);

const Btn = ({ children, onClick, variant="primary", disabled, small, style:s={} }) => {
  const variants = {
    primary: { background:G.accent, color:"#000" },
    ghost:   { background:"#1A1A1A", color:G.muted, border:`1px solid #2A2A2A` },
    danger:  { background:G.danger+"22", color:G.danger, border:`1px solid ${G.danger}44` },
  };
  return (
    <button style={{ border:"none", cursor:disabled?"default":"pointer", fontFamily:G.mono, fontWeight:500, borderRadius:24, padding:small?"5px 14px":"9px 22px", fontSize:small?11:13, letterSpacing:0.3, transition:"opacity 0.15s", opacity:disabled?0.4:1, ...variants[variant], ...s }}
      onClick={onClick} disabled={disabled}
      onMouseOver={e => { if(!disabled) e.currentTarget.style.opacity="0.8"; }}
      onMouseOut={e => { e.currentTarget.style.opacity=disabled?"0.4":"1"; }}>
      {children}
    </button>
  );
};

const Input = ({ label, helper, error, ...props }) => (
  <div style={{ marginBottom:16 }}>
    {label && <label style={{ display:"block", fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>{label}</label>}
    <input style={{ width:"100%", background:"#141414", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"11px 14px", fontSize:14, fontFamily:G.font, outline:"none", boxSizing:"border-box" }}
      onFocus={e => e.target.style.borderColor=G.accent}
      onBlur={e => e.target.style.borderColor="#2A2A2A"}
      {...props} />
    {error ? <div style={{ fontSize:12, color:G.danger, marginTop:6, fontFamily:G.mono }}>{error}</div>
      : helper && <div style={{ fontSize:12, color:G.muted, marginTop:6, fontFamily:G.mono }}>{helper}</div>}
  </div>
);

const Toast = ({ msg, type="success" }) => msg ? (
  <div style={{ position:"fixed", top:20, right:20, zIndex:9999, background:type==="error"?G.danger:G.accent, color:type==="error"?"#fff":"#000", padding:"11px 20px", borderRadius:8, fontFamily:G.mono, fontSize:13, fontWeight:600, boxShadow:"0 4px 24px rgba(0,0,0,0.5)" }}>{msg}</div>
) : null;

const Avatar = ({ initials, size=32, color=G.accent }) => (
  <div style={{ width:size, height:size, borderRadius:"50%", background:"#1A1A1A", border:`1.5px solid ${color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.3, fontWeight:700, color, fontFamily:G.mono, flexShrink:0 }}>{initials?.slice(0,2).toUpperCase()}</div>
);

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [topics, setTopics] = useState([]);
  const [showTopics, setShowTopics] = useState(false);

  useEffect(() => { topicsAPI.list().then(r => setTopics(r.data.topics || [])).catch(() => {}); }, []);

  const loadNotifs = async () => {
    try {
      const res = await notificationsAPI.list();
      setNotifications(res.data.notifications);
      setUnread(res.data.unread);
    } catch {}
  };

  useEffect(() => {
    if (user) {
      loadNotifs();
      const t = setInterval(loadNotifs, 30000);
      return () => clearInterval(t);
    }
  }, [user]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) { navigate(`/search?q=${encodeURIComponent(search.trim())}`); setMenuOpen(false); }
  };

  return (
    <>
      <nav style={{ background:"#0D0D0D", borderBottom:`1px solid ${G.border}`, padding:"0 16px", display:"flex", alignItems:"center", justifyContent:"space-between", height:56, position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <Link to={user ? "/feed" : "/"} style={{ textDecoration:"none", display:"flex", alignItems:"center", gap:9 }} onClick={() => setMenuOpen(false)}>

            <span style={{ fontSize:17, letterSpacing:-0.5, fontFamily:G.font }}><span style={{ fontWeight:900, color:G.accent }}>ask</span><span style={{ fontWeight:700, color:G.text }}>FOGRA</span></span>
          </Link>
          <div className="nav-topics" style={{ position:"relative" }}>
            <button onClick={() => setShowTopics(!showTopics)}
              style={{ background:"transparent", border:"none", color:G.muted, fontSize:13, fontFamily:G.mono, cursor:"pointer", display:"flex", alignItems:"center", gap:4, padding:0 }}>
              Topics <span style={{ fontSize:9 }}>▾</span>
            </button>
            {showTopics && (
              <div style={{ position:"absolute", top:28, left:0, width:220, maxHeight:340, overflowY:"auto", background:"#141414", border:`1px solid ${G.border}`, borderRadius:12, boxShadow:"0 8px 32px rgba(0,0,0,0.5)", zIndex:200 }}>
                {topics.map(t => (
                  <Link key={t.id} to={`/topics/${t.slug}`} onClick={() => setShowTopics(false)}
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", color:G.text, textDecoration:"none", fontSize:13, borderBottom:`1px solid ${G.border}` }}
                    onMouseOver={e => e.currentTarget.style.background="#1E1E1E"}
                    onMouseOut={e => e.currentTarget.style.background="transparent"}>
                    <span style={{ width:8, height:8, borderRadius:"50%", background:t.color||G.accent, flexShrink:0 }} />
                    {t.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
            {user && <Link to="/listings" className="nav-listings" style={{ textDecoration:"none", color:G.muted, fontSize:13, fontFamily:G.mono }}>Listings</Link>}
          <form onSubmit={handleSearch} className="nav-search" style={{ position:"relative" }}>
            <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#444", fontSize:13 }}>⌕</span>
            <input placeholder="Search questions..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ background:"#161616", border:`1px solid #252525`, color:G.text, padding:"7px 14px 7px 30px", borderRadius:20, fontSize:13, width:220, outline:"none", fontFamily:G.font }} />
          </form>
        </div>
        <div className="nav-desktop-actions" style={{ display:"flex", alignItems:"center", gap:10 }}>
          {user ? (
            <>
              <Btn small onClick={() => navigate("/ask")}>+ Ask</Btn>
              <div style={{ position:"relative" }}>
                <button onClick={async () => {
                  setShowNotifs(!showNotifs);
                  if (!showNotifs) { await loadNotifs(); await notificationsAPI.readAll(); setUnread(0); }
                }} style={{ background:"#1A1A1A", border:"1px solid #2A2A2A", color:unread>0?G.accent:G.muted, width:34, height:34, borderRadius:"50%", cursor:"pointer", fontSize:16, position:"relative" }}>
                  🔔
                  {unread > 0 && <span style={{ position:"absolute", top:-4, right:-4, background:G.danger, color:"#fff", fontSize:9, fontWeight:700, width:16, height:16, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:G.mono }}>{unread}</span>}
                </button>
                {showNotifs && (
                  <div style={{ position:"absolute", top:42, right:0, width:300, background:"#141414", border:`1px solid ${G.border}`, borderRadius:12, boxShadow:"0 8px 32px rgba(0,0,0,0.5)", zIndex:200 }}>
                    <div style={{ padding:"12px 16px", borderBottom:`1px solid ${G.border}`, fontSize:12, color:G.muted, fontFamily:G.mono, letterSpacing:1 }}>NOTIFICATIONS</div>
                    {notifications.length === 0
                      ? <div style={{ padding:"24px 16px", textAlign:"center", color:"#333", fontSize:13 }}>No notifications yet</div>
                      : notifications.map(n => (
                        <div key={n.id} onClick={() => { setShowNotifs(false); navigate(n.link||"/"); }}
                          style={{ padding:"12px 16px", borderBottom:`1px solid ${G.border}`, cursor:"pointer", background:n.is_read?"transparent":"#1A1A1A" }}
                          onMouseOver={e => e.currentTarget.style.background="#1E1E1E"}
                          onMouseOut={e => e.currentTarget.style.background=n.is_read?"transparent":"#1A1A1A"}>
                          <div style={{ fontSize:13, color:G.text, lineHeight:1.4, marginBottom:4 }}>{n.message}</div>
                          <div style={{ fontSize:11, color:"#444", fontFamily:G.mono }}>{new Date(n.created_at).toLocaleDateString()}</div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              <Link to={`/u/${user.username}`} style={{ textDecoration:"none" }}><Avatar initials={user.username} size={32} /></Link>
              {user?.is_admin && <Btn small variant="ghost" onClick={() => navigate("/admin")}>⚙️ Admin</Btn>}
              <Btn small variant="ghost" onClick={() => { logout(); window.location.href="/"; }}>Logout</Btn>
            </>
          ) : (
            <>
              <Btn small variant="ghost" onClick={() => navigate("/login")}>Sign In</Btn>
              <Btn small onClick={() => navigate("/register")}>Sign Up</Btn>
            </>
          )}
        </div>
        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen?"✕":"☰"}</button>
      </nav>
      <div className={`mobile-menu ${menuOpen?"open":""}`}>
        <form onSubmit={handleSearch} style={{ position:"relative", marginBottom:8 }}>
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#444", fontSize:13 }}>⌕</span>
          <input placeholder="Search questions..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width:"100%", background:"#161616", border:`1px solid #252525`, color:G.text, padding:"10px 14px 10px 32px", borderRadius:8, fontSize:14, outline:"none", fontFamily:G.font, boxSizing:"border-box" }} />
        </form>
        <Link to="/feed" onClick={() => setMenuOpen(false)}>🏠 Feed</Link>
          {user && <Link to="/listings" onClick={() => setMenuOpen(false)}>🏢 Listings</Link>}
        {topics.map(t => (
          <Link key={t.id} to={`/topics/${t.slug}`} onClick={() => setMenuOpen(false)}>🏷️ {t.name}</Link>
        ))}
        {user ? (
          <>
            <Link to="/ask" onClick={() => setMenuOpen(false)}>✏️ Ask a Question</Link>
            <Link to={`/u/${user.username}`} onClick={() => setMenuOpen(false)}>👤 Profile ({user.username})</Link>
            {user.is_admin && <Link to="/admin" onClick={() => setMenuOpen(false)}>⚙️ Admin Dashboard</Link>}
            <button onClick={() => { logout(); window.location.href="/"; }} style={{ color:G.danger, borderColor:G.danger+"44" }}>🚪 Logout</button>
          </>
        ) : (
          <>
            <Link to="/login" onClick={() => setMenuOpen(false)}>🔑 Sign In</Link>
            <Link to="/register" onClick={() => setMenuOpen(false)}>✨ Sign Up</Link>
          </>
        )}
      </div>
    </>
  );
};

const QuestionCard = ({ q, onVote }) => {
  const navigate = useNavigate();
  return (
    <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:"16px 20px", marginBottom:10, cursor:"pointer", transition:"border-color 0.15s, transform 0.1s" }}
      onMouseOver={e => { e.currentTarget.style.borderColor="#2A2A2A"; e.currentTarget.style.transform="translateY(-1px)"; }}
      onMouseOut={e => { e.currentTarget.style.borderColor=G.border; e.currentTarget.style.transform="translateY(0)"; }}>
      <div style={{ display:"flex", gap:14 }}>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, paddingTop:2 }}>
          <button onClick={e => { e.stopPropagation(); onVote(q.id,1); }} style={{ background:q.user_vote===1?G.accent:"#1A1A1A", border:`1px solid ${q.user_vote===1?G.accent:"#333"}`, color:q.user_vote===1?"#000":G.muted, width:30, height:26, borderRadius:6, cursor:"pointer", fontSize:12 }}>▲</button>
          <span style={{ fontFamily:G.mono, fontSize:13, fontWeight:700, color:q.user_vote?G.accent:"#AAA" }}>{q.vote_score??0}</span>
        </div>
        <div style={{ flex:1 }} onClick={() => navigate(`/q/${q.slug}`)}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
            {q.topic_name && <Tag color={q.topic_color||G.purple}>{q.topic_name}</Tag>}
            <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>{q.created_at?new Date(q.created_at).toLocaleDateString():""}</span>
          </div>
          <div style={{ fontSize:15, fontWeight:600, color:G.text, lineHeight:1.4, marginBottom:6 }}>{q.title}</div>
          {q.body && <div style={{ fontSize:13, color:G.muted, lineHeight:1.5, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{q.body}</div>}
          <div style={{ display:"flex", alignItems:"center", gap:14, marginTop:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <Avatar initials={q.author_username} size={20} />
              <span style={{ fontSize:12, color:G.muted }}>{q.author_username}</span>
            </div>
            <span style={{ fontSize:12, color:"#3A3A3A" }}>💬 {q.answer_count??0}</span>
            <span style={{ fontSize:12, color:"#3A3A3A" }}>👁 {q.views??0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeedPage = () => {
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [topic, setTopic]         = useState("all");
  const [sort, setSort]           = useState("trending");
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const [toast, setToast]         = useState(null);
  const [followedTopics, setFollowedTopics] = useState([]);
  const { user } = useAuth();
  const notify = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),2500); };
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    // Guard against out-of-order responses: these queries can take several
    // seconds, so a slow request for a sort the user already clicked away
    // from must not be allowed to overwrite a newer, faster response.
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      let res;
      if (search) {
        res = await questionsAPI.search({ q:search, page, limit:15 });
      } else if (sort === "foryou" && user) {
        res = await questionsAPI.feed({ page, limit:15 });
      } else {
        const p = { sort, page, limit:15 };
        if (topic !== "all") p.topic = topic;
        res = await questionsAPI.list(p);
      }
      if (requestId !== requestIdRef.current) return;
      setQuestions(res.data.questions);
      setTotal(res.data.total);
    } catch {
      if (requestId !== requestIdRef.current) return;
      notify("Failed to load questions","error");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [topic, sort, page, search, user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { topicsAPI.list().then(r => setTopics(r.data.topics)).catch(()=>{}); }, []);

  const handleVote = async (id, value) => {
    if (!user) return notify("Login to vote","error");
    try { await questionsAPI.vote(id,value); load(); }
    catch (e) { notify(e.response?.data?.error||"Vote failed","error"); }
  };

  const handleFollowTopic = async (topicId) => {
    if (!user) return notify("Login to follow topics","error");
    try {
      const res = await topicsAPI.follow(topicId);
      if (res.data.following) setFollowedTopics(prev => [...prev, topicId]);
      else setFollowedTopics(prev => prev.filter(id => id !== topicId));
      notify(res.data.following ? "Topic followed! Check ✨ For You feed" : "Topic unfollowed");
    } catch { notify("Failed","error"); }
  };

  return (
    <div className="feed-grid" style={{ display:"flex", gap:24, maxWidth:980, margin:"0 auto", padding:"24px 16px" }}>
      <SEO title="Feed" description="Browse the latest questions and answers from the askFOGRA community." url="/feed" noindex />
      <Toast msg={toast?.msg} type={toast?.type} />
      <div className="sidebar" style={{ width:190, flexShrink:0 }}>
        <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:16, marginBottom:14 }}>
          <div style={{ fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Topics</div>
          {[{slug:"all", name:"All", id:null}, ...topics].map(t => (
            <div key={t.slug} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:2 }}>
              <div onClick={() => { setTopic(t.slug); setSort("newest"); setPage(1); setQuestions([]); }} style={{ flex:1, padding:"6px 10px", borderRadius:6, cursor:"pointer", fontSize:13, color:topic===t.slug?G.accent:G.muted, background:topic===t.slug?G.accent+"15":"transparent", transition:"all 0.15s" }}>{t.name}</div>
              {t.id && user && (
                <button onClick={() => handleFollowTopic(t.id)} title={followedTopics.includes(t.id)?"Unfollow":"Follow topic"}
                  style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, color:followedTopics.includes(t.id)?G.accent:"#333", padding:"2px 6px", transition:"color 0.15s" }}>
                  {followedTopics.includes(t.id)?"★":"☆"}
                </button>
              )}
            </div>
          ))}
        </div>
        <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:16 }}>
          <div style={{ fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Sort</div>
          {[["foryou","✨ For You"],["trending","🔥 Trending"],["newest","🆕 Newest"],["unanswered","❓ Unanswered"]].map(([v,l]) => (
            <div key={v} onClick={() => { setSort(v); setPage(1); setQuestions([]); }} style={{ padding:"6px 10px", borderRadius:6, cursor:"pointer", fontSize:13, color:sort===v?G.purple:G.muted, background:sort===v?G.purple+"15":"transparent", transition:"all 0.15s", marginBottom:2 }}>{l}</div>
          ))}
        </div>
      </div>
      <div style={{ flex:1 }}>
        {search && <div style={{ marginBottom:14, fontSize:13, color:G.muted }}>Results for "<span style={{ color:G.text }}>{search}</span>"</div>}
        <div style={{ marginBottom:12, fontSize:12, color:"#3A3A3A", fontFamily:G.mono }}>{total} questions</div>
        {loading
          ? <div style={{ textAlign:"center", padding:"60px 0", color:G.muted }}>Loading...</div>
          : questions.length===0
            ? <div style={{ textAlign:"center", padding:"60px 0", color:"#333" }}><div style={{ fontSize:36, marginBottom:10 }}>🔍</div><div>No questions found</div></div>
            : questions.map(q => <QuestionCard key={q.id} q={q} onVote={handleVote} />)
        }
        {total > 15 && (
          <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:20 }}>
            <Btn small variant="ghost" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</Btn>
            <span style={{ padding:"5px 12px", fontFamily:G.mono, fontSize:12, color:G.muted }}>Page {page}</span>
            <Btn small variant="ghost" disabled={page*15>=total} onClick={() => setPage(p=>p+1)}>Next →</Btn>
          </div>
        )}
      </div>
    </div>
  );
};

const QuestionPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [notFound, setNotFound]       = useState(false);
  const [answerBody, setAnswerBody]   = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [toast, setToast]             = useState(null);
  const [editingQ, setEditingQ]       = useState(false);
  const [editQForm, setEditQForm]     = useState({ title:"", body:"" });
  const [editingA, setEditingA]       = useState(null);
  const [editABody, setEditABody]     = useState("");
  const [promptAction, setPromptAction] = useState(null);
  const [bookmarked, setBookmarked]   = useState(false);
  const [relatedQuestions, setRelatedQuestions] = useState([]);
  const answerRef = useRef(null);
  const editRef   = useRef(null);
  const notify = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),2500); };
  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await questionsAPI.get(slug);
      setData(res.data);
    } catch (e) {
      // Only a genuine 404 from the API means this slug doesn't exist.
      // Any other failure (timeout, 5xx, network hiccup) must NOT be
      // treated as not-found — that would falsely noindex a real question
      // just because a request failed transiently.
      if (e.response?.status === 404) setNotFound(true);
      else notify("Failed to load question","error");
    } finally {
      setLoading(false);
    }
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const topicId = data?.question?.topic_id;
    const questionId = data?.question?.id;
    if (!topicId || !questionId) { setRelatedQuestions([]); return; }
    questionsAPI.list({ topic:topicId, limit:10, exclude:questionId })
      .then(res => setRelatedQuestions(res.data.questions || []))
      .catch(() => setRelatedQuestions([]));
  }, [data?.question?.id, data?.question?.topic_id]);
  const handleVoteQ = async (value) => {
    if (!user) return setPromptAction("vote");
    try { await questionsAPI.vote(data.question.id,value); load(); }
    catch (e) { notify(e.response?.data?.error||"Vote failed","error"); }
  };
  const handleVoteA = async (id, value) => {
    if (!user) return setPromptAction("vote");
    try { await answersAPI.vote(id,value); load(); }
    catch (e) { notify(e.response?.data?.error||"Vote failed","error"); }
  };
  const handleBookmark = async () => {
    if (!user) return setPromptAction("save");
    try { const res = await questionsAPI.bookmark(data.question.id); setBookmarked(res.data.bookmarked); notify(res.data.bookmarked?"Bookmarked!":"Bookmark removed"); }
    catch (e) { notify(e.response?.data?.error||"Failed","error"); }
  };
  const handleAccept = async (id) => {
    try { await answersAPI.accept(id); load(); notify("Answer accepted!"); }
    catch (e) { notify(e.response?.data?.error||"Failed","error"); }
  };
  const handleAnswer = async () => {
    if (!user) return notify("Login to answer","error");
    if (answerBody.trim().length < 20) return notify("Answer must be at least 20 characters","error");
    setSubmitting(true);
    try { await answersAPI.create({ body:answerBody, question_id:data.question.id }); setAnswerBody(""); notify("Answer posted!"); load(); }
    catch (e) { notify(e.response?.data?.errors?.[0]?.msg||"Failed to post answer","error"); }
    finally { setSubmitting(false); }
  };
  const handleEditQ = () => { setEditQForm({ title:data.question.title, body:data.question.body||"" }); setEditingQ(true); };
  const handleSaveQ = async () => {
    try { await questionsAPI.update(data.question.id, editQForm); setEditingQ(false); notify("Question updated!"); load(); }
    catch (e) { notify(e.response?.data?.error||"Update failed","error"); }
  };
  const handleEditA = (a) => { setEditingA(a.id); setEditABody(a.body); };
  const handleSaveA = async (id) => {
    try { await answersAPI.update(id, { body:editABody }); setEditingA(null); notify("Answer updated!"); load(); }
    catch (e) { notify(e.response?.data?.error||"Update failed","error"); }
  };
  const handleDeleteQ = async () => {
    if (!window.confirm("Delete this question? This cannot be undone.")) return;
    try { await questionsAPI.delete(data.question.id); notify("Question deleted!"); setTimeout(() => window.location.href="/", 1000); }
    catch (e) { notify(e.response?.data?.error||"Delete failed","error"); }
  };
  const handleDeleteA = async (id) => {
    if (!window.confirm("Delete this answer?")) return;
    try { await answersAPI.delete(id); notify("Answer deleted!"); load(); }
    catch (e) { notify(e.response?.data?.error||"Delete failed","error"); }
  };
  // Loading: mount an SEO instance that can only ever emit index,follow —
  // noindex is hardcoded false here, never derived from any state that
  // could flip it. This keeps a definitive index,follow robots signal
  // present during hydration instead of leaving it ambiguous.
  if (loading) return (
    <div style={{ textAlign:"center", padding:"80px 0", color:G.muted }}>
      <SEO url={`/q/${slug}`} />
      Loading...
    </div>
  );
  // Genuine 404 from the API — this slug does not exist. Safe to noindex.
  if (notFound) return (
    <div style={{ textAlign:"center", padding:"80px 0", color:G.danger }}>
      <SEO title="Question Not Found" noindex />
      Question not found.
    </div>
  );
  // Any other failure (non-404, e.g. timeout/5xx/network hiccup): still
  // index,follow, same as loading — a transient error is not a 404 and must
  // never be able to mark a real question noindex.
  if (!data) return (
    <div style={{ textAlign:"center", padding:"80px 0", color:G.danger }}>
      <SEO url={`/q/${slug}`} />
      Failed to load question. Please try again.
    </div>
  );
  const { question:q, answers } = data;
  const seoDesc = q.body ? q.body.slice(0,160) : `${q.answer_count} answers on askFOGRA`;
  const acceptedAnswer = answers.find(a => a.is_accepted);
  const qaJsonLd = {
    "@context": "https://schema.org",
    "@type": "QAPage",
    "name": q.title,
    "mainEntity": {
      "@type": "Question",
      "name": q.title,
      "text": q.body || q.title,
      "answerCount": answers.length,
      "dateCreated": q.created_at,
      "dateModified": q.updated_at,
      "upvoteCount": q.vote_score,
      "url": `https://askfogra.com/q/${q.slug}`,
      "author": { "@type": "Person", "name": q.author_username },
      ...(acceptedAnswer && {
        "acceptedAnswer": {
          "@type": "Answer",
          "text": acceptedAnswer.body,
          "dateCreated": acceptedAnswer.created_at,
          "upvoteCount": acceptedAnswer.vote_score,
          "url": `https://askfogra.com/q/${q.slug}`,
          "author": { "@type": "Person", "name": acceptedAnswer.author_username }
        }
      }),
      ...(answers.filter(a => !a.is_accepted).length > 0 && {
        "suggestedAnswer": answers.filter(a => !a.is_accepted).slice(0, 3).map(a => ({
          "@type": "Answer",
          "text": a.body,
          "dateCreated": a.created_at,
          "upvoteCount": a.vote_score,
          "url": `https://askfogra.com/q/${q.slug}`,
          "author": { "@type": "Person", "name": a.author_username }
        }))
      })
    }
  };
  return (
    <div className="question-detail" style={{ display:"flex", gap:24, maxWidth:1200, margin:"0 auto", padding:"28px 16px" }}>
    <div style={{ flex:1, minWidth:0 }}>
      <SEO title={q.title} description={seoDesc} url={`/q/${q.slug}`} type="article" jsonLd={qaJsonLd} noindex={q.is_canonical === false} />
      <Toast msg={toast?.msg} type={toast?.type} />
      {promptAction && <SignInPrompt action={promptAction} onClose={() => setPromptAction(null)} />}
      <Link to="/" style={{ textDecoration:"none", color:G.muted, fontSize:13, fontFamily:G.mono }}>← Back to Feed</Link>
      <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:14, padding:28, margin:"18px 0" }}>
        <div style={{ display:"flex", gap:20 }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
            <button onClick={() => handleVoteQ(1)} style={{ background:q.user_vote===1?G.accent:"#1A1A1A", border:`1px solid ${q.user_vote===1?G.accent:"#333"}`, color:q.user_vote===1?"#000":G.muted, width:30, height:26, borderRadius:6, cursor:"pointer", fontSize:13 }}>▲</button>
            <span style={{ fontFamily:G.mono, fontSize:14, fontWeight:700, color:q.user_vote?G.accent:"#AAA" }}>{q.vote_score}</span>
            <button onClick={() => handleVoteQ(-1)} style={{ background:"#1A1A1A", border:"1px solid #333", color:G.muted, width:30, height:26, borderRadius:6, cursor:"pointer", fontSize:13 }}>▼</button>
          </div>
          <div style={{ flex:1 }}>
            {editingQ ? (
              <div>
                <input value={editQForm.title} onChange={e => setEditQForm({...editQForm,title:e.target.value})}
                  style={{ width:"100%", background:"#0D0D0D", border:`1px solid ${G.accent}`, color:G.text, borderRadius:8, padding:"10px 14px", fontSize:16, fontFamily:G.font, outline:"none", boxSizing:"border-box", marginBottom:10 }} />
                <textarea value={editQForm.body} onChange={e => setEditQForm({...editQForm,body:e.target.value})} rows={4}
                  style={{ width:"100%", background:"#0D0D0D", border:`1px solid #333`, color:G.text, borderRadius:8, padding:"10px 14px", fontSize:14, fontFamily:G.font, outline:"none", resize:"vertical", lineHeight:1.6, boxSizing:"border-box", marginBottom:10 }} />
                <div style={{ display:"flex", gap:8 }}>
                  <Btn small onClick={handleSaveQ}>Save</Btn>
                  <Btn small variant="ghost" onClick={() => setEditingQ(false)}>Cancel</Btn>
                </div>
              </div>
            ) : (
              <div>
                {q.topic_name && <Tag color={q.topic_color||G.purple}>{q.topic_name}</Tag>}
                <h1 style={{ fontSize:22, fontWeight:700, color:G.text, margin:"10px 0 12px", lineHeight:1.3 }}>{q.title}</h1>
                {q.body && <MarkdownRenderer content={q.body} />}
                <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:16, paddingTop:14, borderTop:`1px solid ${G.border}`, flexWrap:"wrap" }}>
                  <Avatar initials={q.author_username} size={26} />
                  <span style={{ fontSize:13, color:G.muted }}>{q.author_username}</span>
                  <span style={{ fontSize:12, color:"#333", fontFamily:G.mono }}>{new Date(q.created_at).toLocaleDateString()}</span>
                  <span style={{ fontSize:12, color:"#333" }}>👁 {q.views} views</span>
                  <Btn small variant="ghost" onClick={handleBookmark}>{bookmarked?"★ Saved":"☆ Save"}</Btn>
                  {user?.id === q.author_id && (
                    <>
                      <Btn small variant="ghost" onClick={handleEditQ}>✏️ Edit</Btn>
                      <Btn small variant="danger" onClick={handleDeleteQ}>🗑 Delete</Btn>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <h3 style={{ color:G.muted, fontSize:12, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:14 }}>{answers.length} Answer{answers.length!==1?"s":""}</h3>
      {answers.map(a => (
        <div key={a.id} style={{ background:G.surface, border:`1px solid ${a.is_accepted?G.accent:G.border}`, borderRadius:12, padding:22, marginBottom:12, position:"relative" }}>
          {a.is_accepted && <div style={{ position:"absolute", top:-1, right:18, background:G.accent, color:"#000", fontSize:9, fontWeight:700, padding:"3px 10px", borderRadius:"0 0 8px 8px", fontFamily:G.mono }}>✓ ACCEPTED</div>}
          <div style={{ display:"flex", gap:18 }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <button onClick={() => handleVoteA(a.id,1)} style={{ background:a.user_vote===1?G.accent:"#1A1A1A", border:`1px solid ${a.user_vote===1?G.accent:"#333"}`, color:a.user_vote===1?"#000":G.muted, width:30, height:26, borderRadius:6, cursor:"pointer", fontSize:13 }}>▲</button>
              <span style={{ fontFamily:G.mono, fontSize:13, fontWeight:700, color:"#AAA" }}>{a.vote_score}</span>
              <button onClick={() => handleVoteA(a.id,-1)} style={{ background:"#1A1A1A", border:"1px solid #333", color:G.muted, width:30, height:26, borderRadius:6, cursor:"pointer", fontSize:13 }}>▼</button>
            </div>
            <div style={{ flex:1 }}>
              {editingA === a.id ? (
                <div>
                  <MarkdownToolbar textareaRef={editRef} value={editABody} onChange={setEditABody} />
                  <textarea ref={editRef} value={editABody} onChange={e => setEditABody(e.target.value)} rows={5}
                    style={{ width:"100%", background:"#0D0D0D", border:"1px solid #333", color:G.text, borderRadius:"0 0 8px 8px", padding:"12px 14px", fontSize:14, fontFamily:G.font, lineHeight:1.7, resize:"vertical", outline:"none", boxSizing:"border-box" }} />
                  <div style={{ display:"flex", gap:8, marginTop:10 }}>
                    <Btn small onClick={() => handleSaveA(a.id)}>Save</Btn>
                    <Btn small variant="ghost" onClick={() => setEditingA(null)}>Cancel</Btn>
                  </div>
                </div>
              ) : (
                <div>
                  <MarkdownRenderer content={a.body} />
                  <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", marginTop:12 }}>
                    <Avatar initials={a.author_username} size={22} color={G.purple} />
                    <span style={{ fontSize:12, color:G.muted }}>{a.author_username}</span>
                    <span style={{ fontSize:11, color:"#333", fontFamily:G.mono }}>{new Date(a.created_at).toLocaleDateString()}</span>
                    {a.updated_at !== a.created_at && <span style={{ fontSize:11, color:"#333", fontFamily:G.mono }}>(edited)</span>}
                    {user?.id === q.author_id && !a.is_accepted && (
                      <Btn small variant="ghost" onClick={() => handleAccept(a.id)}>✓ Accept</Btn>
                    )}
                    {user?.id === a.author_id && (
                      <>
                        <Btn small variant="ghost" onClick={() => handleEditA(a)}>✏️ Edit</Btn>
                        <Btn small variant="danger" onClick={() => handleDeleteA(a.id)}>🗑 Delete</Btn>
                      </>
                    )}
                  </div>
                  <Comments answerId={a.id} />
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:22, marginTop:20 }}>
        <h3 style={{ color:G.muted, fontSize:12, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:14 }}>Your Answer</h3>
        {user ? (
          <>
            <MarkdownToolbar textareaRef={answerRef} value={answerBody} onChange={setAnswerBody} />
            <textarea ref={answerRef} value={answerBody} onChange={e => setAnswerBody(e.target.value)} rows={6}
              placeholder="Write a clear, specific answer. Minimum 20 characters."
              style={{ width:"100%", background:"#0D0D0D", border:"1px solid #222", color:G.text, borderRadius:"0 0 8px 8px", padding:"12px 14px", fontSize:14, fontFamily:G.font, lineHeight:1.7, resize:"vertical", outline:"none", boxSizing:"border-box" }}
              onFocus={e => e.target.style.borderColor=G.accent}
              onBlur={e => e.target.style.borderColor="#222"} />
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12 }}>
              <span style={{ fontSize:11, color:"#333", fontFamily:G.mono }}>{answerBody.length}/10000</span>
              <Btn onClick={handleAnswer} disabled={submitting||answerBody.trim().length<20}>{submitting?"Posting...":"Post Answer →"}</Btn>
            </div>
          </>
        ) : (
          <SignInPrompt action="answer" inline />
        )}
      </div>
    </div>
    {q.topic_id && (
      <div className="sidebar" style={{ width:300, flexShrink:0, position:"sticky", top:76, alignSelf:"flex-start" }}>
        <div style={{ background:"#111111", border:"1px solid #1E1E1E", borderRadius:12, padding:16 }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:G.text, margin:"0 0 4px" }}>Related Questions</h3>
          <p style={{ fontSize:12, color:G.muted, margin:"0 0 14px" }}>More from {q.topic_name}</p>
          {relatedQuestions.length === 0 ? (
            <div style={{ fontSize:12, color:"#333", padding:"12px 0" }}>No related questions yet.</div>
          ) : relatedQuestions.map(rq => (
            <Link key={rq.id} to={`/q/${rq.slug}`} style={{ textDecoration:"none" }}>
              <div style={{ padding:"10px 0", borderBottom:"1px solid #1A1A1A", transition:"opacity 0.15s" }}
                onMouseOver={e => e.currentTarget.style.opacity="0.7"}
                onMouseOut={e => e.currentTarget.style.opacity="1"}>
                <p style={{ fontSize:13, color:"#E4E4DC", lineHeight:1.4, margin:"0 0 6px", overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{rq.title}</p>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11, color:"#888888", fontFamily:G.mono }}>{rq.answer_count??0} answer{rq.answer_count!==1?"s":""}</span>
                  {rq.topic_name && <Tag color={rq.topic_color||G.purple}>{rq.topic_name}</Tag>}
                </div>
              </div>
            </Link>
          ))}
          <Link to={`/topics/${q.topic_slug}`} style={{ display:"block", marginTop:14, fontSize:12, color:"#00C896", textDecoration:"none", fontFamily:G.mono }}>
            See all {q.topic_name} questions →
          </Link>
        </div>
      </div>
    )}
    </div>
  );
};

const AskPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics]         = useState([]);
  const [listings, setListings]     = useState([]);
  const [form, setForm]             = useState({ title:"", body:"", topic_id:"", listing_id:"" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  useEffect(() => {
    if (!user) navigate("/login");
    topicsAPI.list().then(r => setTopics(r.data.topics)).catch(()=>{});
    listingsAPI.list({ limit:50 }).then(r => setListings(r.data.listings)).catch(()=>{});
  }, [user, navigate]);
  const handleSubmit = async () => {
    if (form.title.trim().length < 10) return setError("Title must be at least 10 characters");
    setSubmitting(true); setError("");
    try {
      const res = await questionsAPI.create({
        title:form.title, body:form.body||undefined,
        topic_id:form.topic_id?Number(form.topic_id):undefined,
        listing_id:form.listing_id||undefined,
      });
      navigate(`/q/${res.data.question.slug}`);
    } catch (e) { setError(e.response?.data?.errors?.[0]?.msg||e.response?.data?.error||"Failed to post"); }
    finally { setSubmitting(false); }
  };
  return (
    <div style={{ maxWidth:700, margin:"0 auto", padding:"36px 16px" }}>
      <SEO title="Ask a Question" noindex />
      <Link to="/" style={{ textDecoration:"none", color:G.muted, fontSize:13, fontFamily:G.mono }}>← Back</Link>
      <h1 style={{ fontSize:26, fontWeight:700, color:G.text, margin:"18px 0 6px" }}>Ask a Question</h1>
      <p style={{ color:G.muted, fontSize:14, marginBottom:28 }}>Be specific. A great question gets great answers.</p>
      <Input label="Question title *" placeholder="e.g. What is the best way to learn PostgreSQL in 2025?" value={form.title} onChange={e => setForm({...form,title:e.target.value})} />
      <div style={{ marginBottom:16 }}>
        <label style={{ display:"block", fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Details (optional)</label>
        <textarea value={form.body} onChange={e => setForm({...form,body:e.target.value})} rows={5}
          placeholder="Add context, what you have already tried..."
          style={{ width:"100%", background:"#141414", border:"1px solid #2A2A2A", color:G.text, borderRadius:8, padding:"12px 14px", fontSize:14, fontFamily:G.font, resize:"vertical", outline:"none", lineHeight:1.6, boxSizing:"border-box" }}
          onFocus={e => e.target.style.borderColor=G.accent}
          onBlur={e => e.target.style.borderColor="#2A2A2A"} />
      </div>
      <div style={{ marginBottom:16 }}>
        <label style={{ display:"block", fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Topic</label>
        <select value={form.topic_id} onChange={e => setForm({...form,topic_id:e.target.value})}
          style={{ width:"100%", background:"#141414", border:"1px solid #2A2A2A", color:G.text, borderRadius:8, padding:"11px 14px", fontSize:14, fontFamily:G.font, outline:"none", cursor:"pointer" }}>
          <option value="">Select a topic...</option>
          {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div style={{ marginBottom:28 }}>
        <label style={{ display:"block", fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Related Listing (optional)</label>
        <select value={form.listing_id} onChange={e => setForm({...form,listing_id:e.target.value})}
          style={{ width:"100%", background:"#141414", border:"1px solid #2A2A2A", color:G.text, borderRadius:8, padding:"11px 14px", fontSize:14, fontFamily:G.font, outline:"none", cursor:"pointer" }}>
          <option value="">Link to a broker or platform...</option>
          {listings.map(l => <option key={l.id} value={l.id}>{l.name} ({l.category})</option>)}
        </select>
      </div>
      {error && <div style={{ color:G.danger, fontSize:13, marginBottom:16, fontFamily:G.mono }}>{error}</div>}
      <Btn onClick={handleSubmit} disabled={submitting||form.title.trim().length<10}>{submitting?"Posting...":"Post Question →"}</Btn>
    </div>
  );
};

const NotFoundPage = () => (
  <div style={{ textAlign:"center", padding:"80px 16px" }}>
    <SEO title="Page Not Found" noindex />
    <div style={{ fontSize:64, marginBottom:16 }}>404</div>
    <h1 style={{ fontSize:24, fontWeight:700, color:"#E4E4DC", marginBottom:12 }}>Page Not Found</h1>
    <p style={{ color:"#666", marginBottom:24 }}>The page you're looking for doesn't exist or has been moved.</p>
    <Link to="/" style={{ color:"#00C896", textDecoration:"none", fontFamily:"'DM Mono', monospace" }}>← Back to Home</Link>
  </div>
);

const AUTH_FIELD_FALLBACK_MESSAGES = {
  username: "Username can only contain letters, numbers and underscores. No spaces or @ symbols.",
  email: "Please enter a valid email address.",
  password: "Password must be at least 6 characters.",
};

const AuthPage = ({ mode }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm]             = useState({ username:"", email:"", password:"" });
  const [error, setError]           = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading]       = useState(false);
  const isLogin = mode === "login";
  const handleSubmit = async () => {
    setError(""); setFieldErrors({}); setLoading(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.username, form.email, form.password);
      navigate("/");
    } catch (e) {
      const data = e.response?.data;
      if (data?.errors?.length) {
        const fe = {};
        data.errors.forEach(err => {
          const field = err.path || err.param;
          fe[field] = !err.msg || err.msg === "Invalid value"
            ? (AUTH_FIELD_FALLBACK_MESSAGES[field] || "Please check this field and try again.")
            : err.msg;
        });
        setFieldErrors(fe);
        setError("Please fix the highlighted fields below.");
      } else {
        setError(data?.error || "Something went wrong. Please try again.");
      }
    }
    finally { setLoading(false); }
  };
  return (
    <div style={{ maxWidth:400, margin:"60px auto", padding:"0 16px" }}>
      <SEO title={isLogin ? "Login" : "Sign Up"} noindex />
      <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:16, padding:32 }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ margin:"0 auto 12px", textAlign:"center" }}><span style={{ fontSize:28, letterSpacing:-1, fontFamily:"'Sora', sans-serif" }}><span style={{ fontWeight:900, color:"#00C896" }}>ask</span><span style={{ fontWeight:700, color:"#E4E4DC" }}>FOGRA</span></span></div>
          <h2 style={{ color:G.text, margin:0, fontSize:20 }}>{isLogin?"Welcome back":"Join askFOGRA"}</h2>
          <p style={{ color:G.muted, fontSize:13, marginTop:6 }}>{isLogin?"Sign in to your account":"Create your account today"}</p>
        </div>
        {!isLogin && <Input label="Username" placeholder="e.g. john_doe (letters, numbers, underscores only)"
          helper="No spaces or @ symbols allowed" error={fieldErrors.username}
          value={form.username} onChange={e => setForm({...form,username:e.target.value})} />}
        <Input label="Email" type="email" placeholder="your@email.com"
          error={fieldErrors.email}
          value={form.email} onChange={e => setForm({...form,email:e.target.value})} />
        <Input label="Password" type="password" placeholder={isLogin?"Your password":"At least 6 characters"}
          helper={isLogin?undefined:"Minimum 6 characters"} error={fieldErrors.password}
          value={form.password} onChange={e => setForm({...form,password:e.target.value})} />
        {error && <div style={{ color:G.danger, fontSize:13, marginBottom:14, fontFamily:G.mono }}>{error}</div>}
        <Btn onClick={handleSubmit} disabled={loading} style={{ width:"100%", marginBottom:16 }}>{loading?"...":isLogin?"Login":"Create Account"}</Btn>
        <div style={{ textAlign:"center", fontSize:13, color:G.muted }}>
          {isLogin?"No account? ":"Already a member? "}
          <Link to={isLogin?"/register":"/login"} style={{ color:G.accent }}>{isLogin?"Sign up":"Login"}</Link>
        </div>
      </div>
    </div>
  );
};


const AppInner = () => {
  const location = useLocation();
  const isLanding = location.pathname === "/";
  return (
    <div style={{ background:G.bg, minHeight:"100vh", color:G.text, fontFamily:G.font }}>
      {!isLanding && <Navbar />}
      <Routes>
        <Route path="/"            element={<LandingPage />} />
        <Route path="/feed"        element={<FeedPage />} />
        <Route path="/q/:slug"     element={<QuestionPage />} />
        <Route path="/ask"         element={<AskPage />} />
        <Route path="/login"       element={<AuthPage mode="login" />} />
        <Route path="/register"    element={<AuthPage mode="register" />} />
        <Route path="/u/:username" element={<ProfilePage />} />
        <Route path="/topics/:slug" element={<TopicPage />} />
        <Route path="/search"      element={<SearchPage />} />
        <Route path="/admin"       element={<AdminPage />} />
        <Route path="/admin/analytics" element={<AnalyticsPage />} />
        <Route path="/listings"    element={<ListingsPage />} />
        <Route path="/listings/new" element={<SubmitListingPage />} />
        <Route path="/listings/:slug" element={<ListingDetailPage />} />
        <Route path="*"            element={<NotFoundPage />} />
      </Routes>
    </div>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </AuthProvider>
  );
}
